import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { Router } from '@angular/router';
import {
  ValidationFinding,
  ValidationRunResult,
} from '../validate-button/validation-run-result';

interface FindingGroup {
  section: string;
  findings: ValidationFinding[];
}

/**
 * Human-friendly labels for known section keys. Unknown keys fall back to the
 * raw key so the panel stays entity-agnostic (renders from the response alone).
 */
/** entityType (Mongoose model name) -> frontend view-route prefix ({prefix}/:id). */
const ROUTE_PREFIX: Record<string, string> = {
  Series: 'series',
  Challenge: 'challenges',
  Item: 'items',
  Collection: 'collections',
  LoadingScreen: 'loading-screens',
  InboxMessage: 'inbox-messages',
  StoreListingV2: 'store-listings-v2',
  Byob: 'byob',
};

const SECTION_LABELS: Record<string, string> = {
  seriesData: 'Series Data',
  challenges: 'Challenges',
  items: 'Items',
  inbox: 'Inbox',
  storeListings: 'Store Listings',
  challengeData: 'Challenge Data',
  collectionData: 'Collection Data',
  itemData: 'Item Data',
  seriesPrizeData: 'Series Completion Rewards',
  loadingScreenData: 'Loading Screen Data',
  announcementInbox: 'Announcement Inbox Record',
  inboxMessage: 'Inbox Message',
  storeListingData: 'Store Listing Data',
  byobData: 'BYOB Page Data',
};

/**
 * Inline, reusable results panel. Renders only from the generic run-result shape
 * — no entityType-specific logic. Failing fields are listed per section with a
 * reason; a fully-passing result is clearly communicated.
 */
@Component({
  selector: 'app-validation-results-panel',
  templateUrl: './validation-results-panel.component.html',
  styleUrls: ['./validation-results-panel.component.sass'],
})
export class ValidationResultsPanelComponent implements OnChanges {
  @Input() result: ValidationRunResult | null = null;
  @Output() cleared = new EventEmitter<void>();

  constructor(private router: Router) {}

  /** Controls the results dialog. Opens automatically when a result arrives. */
  visible = false;

  /**
   * Findings regrouped by their own `section` (not the button's top-level
   * section) so a loading-screen failure surfaced while validating Series Data
   * appears under "Loading Screen Data", etc. Computed once per result (a getter
   * would return new arrays each change-detection cycle, recreating the rows and
   * eating clicks).
   */
  groupedFindings: FindingGroup[] = [];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['result']) {
      if (this.result) this.visible = true;
      this.groupedFindings = this.computeGroups();
    }
  }

  /** Fired when the dialog closes — clears the result upstream. */
  onHide(): void {
    this.visible = false;
    this.cleared.emit();
  }

  private computeGroups(): FindingGroup[] {
    const order: string[] = [];
    const map = new Map<string, ValidationFinding[]>();
    for (const section of this.result?.sections ?? []) {
      for (const finding of section.findings ?? []) {
        if (!map.has(finding.section)) {
          map.set(finding.section, []);
          order.push(finding.section);
        }
        map.get(finding.section)!.push(finding);
      }
    }
    return order.map((section) => ({ section, findings: map.get(section)! }));
  }

  sectionLabel(key: string): string {
    return SECTION_LABELS[key] ?? key;
  }

  /** View-page href for a finding's entity, or null if it can't be linked. */
  entityHref(finding: ValidationFinding): string | null {
    const prefix = ROUTE_PREFIX[finding.entityType];
    if (!prefix || finding.entityId == null) return null;
    return `/${prefix}/${finding.entityId}`;
  }

  /**
   * Plain left-click opens the record (new tab if allowed, else same tab).
   * Modifier / middle / right clicks fall through to the native `[href]`.
   */
  onLinkClick(event: MouseEvent, finding: ValidationFinding): void {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1) {
      return; // let the native anchor open a new tab/window
    }
    event.preventDefault();
    event.stopPropagation();
    this.open(finding);
  }

  /** Opens the record — new tab if the browser allows, otherwise same tab. */
  open(finding: ValidationFinding): void {
    const href = this.entityHref(finding);
    if (!href) return;
    const opened = window.open(href, '_blank');
    if (!opened) {
      this.router.navigateByUrl(href); // popup blocked → same tab
    }
  }
}
